import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Menu, Tab, Tabs } from '@mui/material';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List
} from 'react-virtualized';

import {
  MDBox,
  MDButton,
  MDTypography,
  NotificationItem
} from '@components/index';
import { RootState } from '@store/store';
import { useUpdateNotificationAsReadMutation } from '@api/notifications';

import { markAllUserMessageAsRead } from './slice';
import { MSG_EVENT, NotificationData } from '@DStypes/notifications';
import useUserDetails from '@hooks/useUserDetails';

interface INotificationProps {
  anchorEl: React.MutableRefObject<undefined>;
  handleCloseMenu: () => void;
  openMenu: boolean;
}

const Notification: FC<INotificationProps> = (props) => {
  const cache = new CellMeasurerCache({
    fixedWidth: true,
    defaultHeight: 200
  });

  const { anchorEl, handleCloseMenu, openMenu } = props;
  const [tabValue, setTabValue] = useState<number>(0);

  const listRef = useRef<List>(null);

  const userDetails = useSelector((state: RootState) => state?.userDetails);
  const notificationsData = useSelector(
    (state: RootState) => state?.notificationsData
  );

  const dispatch = useDispatch();

  const { allMessages, unreadMessages = [] } = notificationsData;

  const [markAsRead] = useUpdateNotificationAsReadMutation();
  const { getUserDetails } = useUserDetails();

  const handleSetTabValue = (event: any, newValue: number) =>
    setTabValue(newValue);

  const handleMarkAllAsRead = async () => {
    const msgIdArr = unreadMessages
      ?.filter((msg) => !msg.is_user_message)
      .map((msg) => msg.id);

    if (msgIdArr && msgIdArr.length)
      await markAsRead({
        id: userDetails.user.id as string,
        message_id: msgIdArr
      });
    dispatch(markAllUserMessageAsRead({ notificationsData }));
  };

  const handleAutoSaveTimeChange = () => {
    if (listRef.current) {
      cache.clearAll();
      listRef.current.forceUpdateGrid();
    }
  };

  interface GroupedNotifications {
    [key: string]: NotificationData[];
  }

  const handleMarkAsReadRestAPI = async () => {
    if (unreadMessages?.length) {
      const groupedByEventAndProject: GroupedNotifications = {};

      // Group notifications by unique combination of message_event and message_data.project.id
      unreadMessages
        .filter(
          (notification: NotificationData) =>
            notification.message_event ===
            MSG_EVENT.DATA_RECALCULATION_COMPLETED
        )
        .forEach((notification: NotificationData) => {
          const key = `${MSG_EVENT.DATA_RECALCULATION_COMPLETED}_${
            JSON.parse(notification.message_data)?.project?.id
          }`;

          if (!groupedByEventAndProject[key])
            groupedByEventAndProject[key] = [];

          groupedByEventAndProject[key].push(notification);
        });

      if (Object.values(groupedByEventAndProject)?.length)
        await getUserDetails(userDetails.user.id as string);
      // Update is_read field for each group
      Object.values(groupedByEventAndProject).forEach((group) => {
        // Mark all notifications except the latest as read

        const messageIds = group.slice(1).map((obj) => obj.id);

        if (messageIds.length)
          markAsRead({
            id: userDetails.user.id as string,
            message_id: [...messageIds]
          });
      });
    }
  };

  useEffect(() => {
    if (unreadMessages.length) handleMarkAsReadRestAPI();
  }, [unreadMessages]);

  return (
    <Menu
      anchorEl={anchorEl?.current}
      anchorReference={anchorEl.current}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      open={openMenu}
      onClose={handleCloseMenu}
      sx={{ mt: 7 }}
    >
      <Tabs value={tabValue} onChange={handleSetTabValue} sx={{ mb: 1 }}>
        <Tab label='Unread' />
        <Tab label='All' />
      </Tabs>
      {unreadMessages?.length > 0 && (
        <MDButton
          color='light'
          sx={{
            mb: 1
          }}
          size='small'
          onClick={handleMarkAllAsRead}
        >
          <MDTypography variant='sm' size='sm' color='info'>
            Mark all as read
          </MDTypography>
        </MDButton>
      )}

      <MDBox height='400px' width='400px' overflow='auto'>
        <AutoSizer>
          {({ height, width }) => (
            <List
              ref={listRef}
              width={width}
              height={height}
              rowCount={
                tabValue === 0
                  ? unreadMessages?.length || 1
                  : allMessages?.length || 1
              }
              rowHeight={cache.rowHeight}
              rowRenderer={({ index, key, style, parent }) => (
                <CellMeasurer
                  cache={cache}
                  columnIndex={0}
                  key={key}
                  rowIndex={index}
                  parent={parent}
                >
                  <div key={key} style={style}>
                    {tabValue === 0 ? (
                      unreadMessages?.length ? (
                        <NotificationItem
                          messageObj={unreadMessages[index]}
                          handleAutoSaveTimeChange={handleAutoSaveTimeChange}
                        />
                      ) : (
                        <MDBox
                          height='50px'
                          display='flex'
                          justifyContent='center'
                          alignItems='center'
                        >
                          No more unread messages to show
                        </MDBox>
                      )
                    ) : allMessages?.length ? (
                      <NotificationItem
                        messageObj={allMessages[index]}
                        handleAutoSaveTimeChange={handleAutoSaveTimeChange}
                      />
                    ) : (
                      <MDBox
                        height='50px'
                        display='flex'
                        justifyContent='center'
                        alignItems='center'
                      >
                        No messages to show
                      </MDBox>
                    )}
                  </div>
                </CellMeasurer>
              )}
            />
          )}
        </AutoSizer>
      </MDBox>
    </Menu>
  );
};

export default Notification;
